/**
 * CheckoutReturnListener
 *
 * Detects when a user returns from the external checkout domain via
 * bfcache (back/forward navigation) and ensures cart state is fresh.
 *
 * Adapted from Shapermint's CheckoutReturnListener. Key behaviors:
 * 1. Listens for the `pageshow` event (fires on bfcache restoration)
 * 2. Checks if the referrer is the checkout domain
 * 3. If `te_clear_cart` cookie is set — order was completed; reloads page
 *    so CartProvider picks up the cookie and clears state
 * 4. If no cookie — checkout was abandoned; dispatches a custom event
 *    that the CheckoutAbandonedModal (or any listener) can respond to
 */

import { useEffect } from 'react';
import { env } from '@/lib/utils/env';
import { getCookie, CLEAR_CART_COOKIE } from '@/lib/utils/cookies';

function getCheckoutDomain(): string | null {
  try {
    return new URL(env.CHECKOUT_URL).hostname;
  } catch {
    return null;
  }
}

export function CheckoutReturnListener() {
  useEffect(() => {
    const handler = (event: PageTransitionEvent) => {
      const checkoutDomain = getCheckoutDomain();
      if (!checkoutDomain) return;

      const referrer = document.referrer;
      const nav = performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming | undefined;

      const isReturnFromCheckout =
        event.persisted ||
        (nav?.type === 'back_forward' && referrer.includes(checkoutDomain));

      if (!isReturnFromCheckout) return;

      const clearCartCookie = getCookie(CLEAR_CART_COOKIE);

      if (clearCartCookie === 'true') {
        // Order completed — hide page to prevent flash of stale cart,
        // then reload so CartProvider reads the cookie and clears state.
        document.body.style.opacity = '0';
        globalThis.location.reload();
      } else {
        // Checkout abandoned — cart should remain intact.
        // Dispatch custom event so optional re-engagement UI can respond.
        window.dispatchEvent(new CustomEvent('checkout:abandoned'));
      }
    };

    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);

  return null;
}
